/**
 * Chaos drill participation service (TRAC-548).
 *
 * Local toggle that opts a session into a soft chaos drill. When ON AND the
 * backend has surfaced an active drill_id (via the /api/settings/announcement
 * payload's active_chaos_drill_id field), `useRegionHealth` will inject
 * `X-Chaos-Drill: <drill_id>` on its region-pinned probes. Backend's
 * chaos_middleware then 503s those probes, which feeds the pin-flip
 * detection state machine — letting us observe pin-flip behavior under
 * regional failure without actually breaking the network.
 *
 * Real customers never have the toggle on, so soft drills are completely
 * invisible to them. The backend gates 503 injection on BOTH the header AND
 * a region-scoped SSM record, so even if a customer accidentally toggled it
 * on, no chaos behavior would activate without an active drill.
 *
 * For real (FIS) drills, this toggle is irrelevant — FIS cuts the network
 * at the subnet level, no header opt-in possible.
 */

const PARTICIPATION_KEY = "traci-chaos-participate";

type Listener = (state: { participating: boolean; drillId: string | null }) => void;

class ChaosParticipationService {
  private drillId: string | null = null;
  private listeners: Set<Listener> = new Set();

  getParticipating(): boolean {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem(PARTICIPATION_KEY) === "true";
    } catch {
      return false;
    }
  }

  setParticipating(on: boolean): void {
    if (typeof window === "undefined") return;
    try {
      if (on) {
        localStorage.setItem(PARTICIPATION_KEY, "true");
      } else {
        localStorage.removeItem(PARTICIPATION_KEY);
      }
    } catch {
      /* ignore — quota / private mode */
    }
    this.notify();
  }

  /** Pumped by ChatInterface's announcement fetcher. Pass null when no drill is active. */
  setDrillId(id: string | null): void {
    if (id === this.drillId) return;
    this.drillId = id;
    this.notify();
  }

  getDrillId(): string | null {
    return this.drillId;
  }

  /**
   * Returns headers to merge into a fetch when this session is opted-in AND
   * a drill is currently active. Returns an empty object otherwise — safe
   * to spread unconditionally into a fetch's headers.
   */
  getDrillHeader(): Record<string, string> {
    if (!this.drillId) return {};
    if (!this.getParticipating()) return {};
    return { "X-Chaos-Drill": this.drillId };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    const snapshot = {
      participating: this.getParticipating(),
      drillId: this.drillId,
    };
    for (const l of this.listeners) l(snapshot);
  }
}

export const chaosParticipationService = new ChaosParticipationService();
